<template>
  <div class="page-container my-cash-register">
    <portal to="page-name">Caixa</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <router-link to="/cash-register/settings"
                     class="btn btn-neutral btn-icon btn-action float-right ml-2 tooltip
                            tooltip-left"
                     data-tooltip="Configuração do caixa"
                     v-if="!loading && $can(MANAGE_CASHIER_SETTINGS)">
          <fa-icon :icon="['fal', 'cog']"/>
        </router-link>
        <template v-if="!loading && isOpen">
          <button class="btn btn-gray btn-icon-left float-right ml-2"
                  :class="{loading: printing}" :disabled="printing" @click="printReport">
            <fa-icon :icon="['fal', 'print']"/>Imprimir
          </button>
          <button class="btn btn-error btn-icon-left float-right ml-2"
                  v-if="$can(CLOSE_CASHIER)" @click="closing.show = true">
            <fa-icon :icon="['fal', 'lock']"/>Fechar caixa
          </button>
          <div class="btn-group float-right" v-if="$can(CREATE_CASHIER_CASH_OPERATION)">
            <button class="btn btn-gray btn-icon-left" @click="openOperation('supply')">
              <fa-icon :icon="['fal', 'arrow-down']"/>Suprimento
            </button>
            <button class="btn btn-gray btn-icon-left" @click="openOperation('withdrawal')">
              <fa-icon :icon="['fal', 'arrow-up']"/>Sangria
            </button>
            <button class="btn btn-gray btn-icon-left" @click="openOperation('expense')">
              <fa-icon :icon="['fal', 'money-bill']"/>Despesa
            </button>
          </div>
        </template>
        <h1 class="card-title">{{ title }}</h1>
      </div>

      <div class="card-body" v-if="loading">
        <div class="loading loading-lg"></div>
      </div>

      <div class="card-body" v-else-if="!settings.enabled">
        <div class="empty">
          <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="3x"/></div>
          <p class="empty-title h5">Caixa novo desligado nesta filial</p>
          <p class="empty-subtitle">
            <router-link to="/cash-register/settings" v-if="$can(MANAGE_CASHIER_SETTINGS)">
              Ligar na configuração do caixa
            </router-link>
            <template v-else>Peça ao gestor para ligar ou use o caixa atual.</template>
          </p>
        </div>
      </div>

      <div class="card-body" v-else-if="!isOpen">
        <open-session @opened="loadSession"/>
      </div>

      <div class="card-body" v-else>
        <div class="cr-summary">
          <div class="cr-summary-item text-left">
            <small>Aberto às</small>
            <div class="cr-summary-value">{{ session.opening.date | date('time') }}</div>
          </div>
          <div class="cr-summary-item">
            <small>Fundo de troco</small>
            <div class="cr-summary-value">{{ session.opening.fund | currency }}</div>
          </div>
          <div class="cr-summary-item">
            <small>Recebido</small>
            <div class="cr-summary-value">{{ netReceipts(session) | currency }}</div>
          </div>
          <div class="cr-summary-item" v-for="line in otherMethods" :key="line.method.name">
            <small>{{ line.method.name }}</small>
            <div class="cr-summary-value">{{ line.value | currency }}</div>
          </div>
          <div class="cr-summary-item highlight">
            <small>Dinheiro em caixa</small>
            <div class="cr-summary-value">{{ session.totals.cashBalance | currency }}</div>
          </div>
        </div>

        <div class="cr-panel cr-panel-pending">
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'hand-holding-usd']" class="cr-panel-icon"/>
              Pendentes de pagamento
              <span class="label label-rounded ml-1" v-if="pending.items.length > 0">
                {{ pending.items.length }}
              </span>
            </div>
            <div class="cr-panel-subtitle">Atendimentos particulares da agenda</div>
          </div>

          <div class="cr-toolbar cr-toolbar-selected" v-if="pending.selected.length > 0">
            <div class="cr-toolbar-text">
              <strong>{{ pending.selected.length }}</strong>
              {{ pending.selected.length === 1 ? 'atendimento selecionado' : 'atendimentos' }}
              {{ pending.selected.length === 1 ? '' : 'selecionados' }}
              · <strong>{{ selectedTotal | currency }}</strong>
            </div>
            <div class="cr-toolbar-actions">
              <button class="btn btn-link" @click="pending.selected = []">Limpar seleção</button>
              <button class="btn btn-primary btn-icon btn-icon-left" @click="receiveSelected">
                <fa-icon :icon="['fal', 'hand-holding-usd']"/>
                {{ pending.selected.length === 1 ? 'Receber' : 'Receber juntos' }}
              </button>
            </div>
          </div>
          <div class="cr-toolbar" v-else>
            <div class="cr-toolbar-field">
              <label class="form-label">Período (até 30 dias)</label>
              <st-date-period
                :start-date.sync="pending.filter.startDate"
                :end-date.sync="pending.filter.endDate"
                :periods="periods"
                :max-months="1"
                :start-date-validator="$v.pending.filter.startDate"
                :end-date-validator="$v.pending.filter.endDate"
                @blur:startDate="$v.pending.filter.startDate.$touch()"
                @blur:endDate="$v.pending.filter.endDate.$touch()"
              />
            </div>
            <div class="cr-toolbar-field cr-toolbar-grow">
              <label class="form-label">Paciente</label>
              <input type="text" class="form-input" v-model="pending.filter.search"
                     placeholder="Buscar pelo nome" @keypress.enter="loadPending">
            </div>
            <div class="cr-toolbar-actions">
              <button class="btn btn-secondary btn-icon btn-icon-left" @click="loadPending"
                      :disabled="pending.loading">
                <fa-icon :icon="['fal', 'search']"/>Buscar
              </button>
            </div>
          </div>

          <div class="loading loading-lg cr-panel-loading" v-if="pending.loading"></div>
          <div class="empty cr-panel-empty" v-else-if="pending.items.length === 0">
            <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="2x"/></div>
            <p class="empty-subtitle">
              Nenhum atendimento particular aguardando pagamento no período.
            </p>
          </div>
          <div class="cr-table-scroll" v-else>
            <table class="table table-hover">
              <thead>
              <tr>
                <th style="width: 30px">
                  <label class="form-checkbox">
                    <input type="checkbox"
                           :checked="allSelected"
                           :indeterminate.prop="partiallySelected"
                           @change="toggleAll">
                    <i class="form-icon"></i>
                  </label>
                </th>
                <th style="width: 120px">Hora</th>
                <th>Paciente</th>
                <th class="hide-sm">Profissional</th>
                <th class="hide-sm">Itens</th>
                <th class="text-right">Valor</th>
                <th style="width: 110px"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in pending.items" :key="item.id"
                  :class="{active: pending.selected.includes(item.id)}">
                <td>
                  <label class="form-checkbox">
                    <input type="checkbox" :value="item.id" v-model="pending.selected">
                    <i class="form-icon"></i>
                  </label>
                </td>
                <td>{{ item.startDate | date('datetime') }}</td>
                <td>{{ item.patient ? item.patient.name : '-' }}</td>
                <td class="hide-sm">{{ item.professional ? item.professional.name : '-' }}</td>
                <td class="hide-sm text-ellipsis"
                    style="max-width: 260px">{{ itemNames(item) }}</td>
                <td class="text-right text-bold">{{ item.total | currency }}</td>
                <td class="text-right">
                  <router-link :to="`/cash-register/receive/${item.id}`"
                               class="btn btn-sm btn-primary btn-icon btn-icon-left"
                               v-if="$can(LIST_CASHIER_ATTENDANCES)">
                    <fa-icon :icon="['fal', 'hand-holding-usd']"/>Receber
                  </router-link>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="cr-panel cr-panel-entries">
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'list']" class="cr-panel-icon"/>
              Movimentos deste caixa
            </div>
            <div class="cr-panel-subtitle">
              Recebimentos, estornos, suprimentos, sangrias e despesas
            </div>
          </div>
          <entries-table
            :items="entries.items"
            :loading="entries.loading"
            @reverse="openReverse"
            @refresh="loadEntries"
          />
        </div>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <operation-modal
      v-if="operation.show"
      :show="operation.show"
      :type="operation.type"
      :cash-balance="session ? session.totals.cashBalance : 0"
      :session-bank-account-id="session && session.bankAccount ? session.bankAccount.id : ''"
      :default-category-id="settings.expenseCategory ? settings.expenseCategory.id : ''"
      @close="closeOperation"
    />
    <reverse-modal
      v-if="reverse.show"
      :show="reverse.show"
      :entry="reverse.entry"
      @close="closeReverse"
    />
    <close-modal
      v-if="closing.show && session"
      :show="closing.show"
      :session="session"
      :blind="settings.blindClose !== false"
      :tolerance="Number(settings.differenceTolerance) || 0"
      @close="closeClosing"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { date, minDate, maxDate } from '@/data/validators';
import {
  CLOSE_CASHIER,
  CREATE_CASHIER_CASH_OPERATION,
  LIST_CASHIER_ATTENDANCES,
  LIST_CASHIER_TRANSACTIONS,
  MANAGE_CASHIER_SETTINGS,
} from '@/data/actions/modules/financial';
import Forbidden from '@/components/auth/Forbidden.vue';
import EntriesTable from './EntriesTable.vue';
import OperationModal from '../modals/Operation.vue';
import ReverseModal from '../modals/Reverse.vue';
import CloseModal from '../modals/Close.vue';
import OpenSession from './OpenSession.vue';

export default {
  components: {
    Forbidden,
    EntriesTable,
    OperationModal,
    ReverseModal,
    CloseModal,
    OpenSession,
  },
  data() {
    return {
      CLOSE_CASHIER,
      CREATE_CASHIER_CASH_OPERATION,
      LIST_CASHIER_ATTENDANCES,
      MANAGE_CASHIER_SETTINGS,
      loading: true,
      printing: false,
      settings: { enabled: false },
      session: null,
      periods: [
        { value: 'today', text: 'Hoje' },
        { value: 'yesterday', text: 'Ontem' },
        { value: 'd:-5', text: 'Últimos 5 dias' },
        { value: 'd:-15', text: 'Últimos 15 dias' },
        { value: 'd:-30', text: 'Últimos 30 dias' },
      ],
      pending: {
        loading: false,
        items: [],
        selected: [],
        filter: {
          startDate: moment().format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
          search: '',
        },
      },
      entries: {
        loading: false,
        items: [],
      },
      operation: {
        show: false,
        type: 'supply',
      },
      reverse: {
        show: false,
        entry: null,
      },
      closing: {
        show: false,
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(LIST_CASHIER_TRANSACTIONS) || this.$can(LIST_CASHIER_ATTENDANCES);
    },
    title() {
      const firstName = this.user && this.user.name ? this.user.name.split(' ')[0] : '';
      return firstName ? `Caixa de ${firstName}` : 'Meu caixa';
    },
    isOpen() {
      return !!this.session && this.session.status === 'open';
    },
    selectedTotal() {
      return this.pending.items
        .filter(({ id }) => this.pending.selected.includes(id))
        .reduce((a, b) => a + b.total, 0);
    },
    allSelected() {
      return this.pending.items.length > 0
        && this.pending.selected.length === this.pending.items.length;
    },
    partiallySelected() {
      return this.pending.selected.length > 0 && !this.allSelected;
    },
    otherMethods() {
      if (!this.session || !this.session.totals) return [];
      return (this.session.totals.byMethod || []).filter(({ method }) => method.type !== 'money');
    },
  },
  validations() {
    const { startDate } = this.pending.filter;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      pending: {
        filter: {
          startDate: { required, date },
          endDate: {
            required,
            date,
            minDate: minDate(validStart ? startDate : null),
            maxDate: maxDate(validStart ? moment(startDate).add(29, 'days') : null),
          },
        },
      },
    };
  },
  async mounted() {
    if (!this.hasAccess) {
      this.loading = false;
      return;
    }
    await this.loadSettings();
    await this.loadSession();
  },
  methods: {
    netReceipts(session) {
      const totals = session.totals || {};
      return typeof totals.netReceipts === 'number' ? totals.netReceipts : totals.receipts || 0;
    },
    async loadSettings() {
      try {
        const { data } = await this.$http.get('/cash-register/settings');
        this.settings = data;
      } catch (e) {
        this.$toast.error(e);
      }
    },
    async loadSession() {
      this.loading = true;
      try {
        const { data } = await this.$http.get('/cash-register/session');
        this.$root.$emit('cash-register:session', data);
        if (data.status === 'open') {
          this.session = data;
          await Promise.all([this.loadPending(), this.loadEntries()]);
        } else {
          this.session = null;
        }
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    loadPending() {
      this.$v.pending.filter.$touch();
      if (this.$v.pending.filter.$error) return Promise.resolve();
      this.pending.loading = true;
      const params = {
        startDate: this.pending.filter.startDate,
        endDate: this.pending.filter.endDate,
        limit: 0,
      };
      if (this.pending.filter.search) {
        params.search = this.pending.filter.search;
      }
      return this.$http.get('/cash-register/pending-appointments', { params })
        .then(({ data }) => {
          this.pending.items = data.items;
          const ids = data.items.map(({ id }) => id);
          this.pending.selected = this.pending.selected.filter(id => ids.includes(id));
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.pending.loading = false;
        });
    },
    loadEntries() {
      if (!this.session) return Promise.resolve();
      this.entries.loading = true;
      const params = { sessionId: this.session.id, limit: 0 };
      return this.$http.get('/cash-register/entries', { params })
        .then(({ data }) => {
          this.entries.items = data.items;
        })
        .catch(() => {})
        .finally(() => {
          this.entries.loading = false;
        });
    },
    toggleAll() {
      this.pending.selected = this.allSelected ? [] : this.pending.items.map(({ id }) => id);
    },
    receiveSelected() {
      if (this.pending.selected.length === 1) {
        this.$router.push(`/cash-register/receive/${this.pending.selected[0]}`);
        return;
      }
      this.$router.push({
        path: '/cash-register/receive',
        query: { ids: this.pending.selected.join(',') },
      });
    },
    itemNames(item) {
      return item.items
        .map(line => `${line.expense.name}${line.quantity > 1 ? ` x${line.quantity}` : ''}`)
        .join(', ');
    },
    printReport() {
      this.printing = true;
      this.$file.print(`/cash-register/sessions/${this.session.id}/report`)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    openOperation(type) {
      this.operation.type = type;
      this.operation.show = true;
    },
    closeOperation(saved) {
      this.operation.show = false;
      if (saved) {
        this.loadSession();
      }
    },
    openReverse(entry) {
      this.reverse.entry = entry;
      this.reverse.show = true;
    },
    closeReverse(saved) {
      this.reverse.show = false;
      if (saved) {
        this.loadSession();
      }
    },
    closeClosing(closed) {
      this.closing.show = false;
      if (closed) {
        this.loadSession();
      }
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.my-cash-register {
  .cr-toolbar {
    align-items: flex-end;
    background-color: $light-color;
    border-bottom: $border-color solid $border-width;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    padding: $layout-spacing;
    .cr-toolbar-field {
      flex: 0 0 auto;
      .form-label {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        padding-bottom: .1rem;
      }
    }
    .cr-toolbar-grow {
      flex: 1 1 14rem;
    }
    .cr-toolbar-actions {
      align-items: center;
      display: flex;
      gap: $layout-spacing-sm;
      margin-left: auto;
    }
    .cr-toolbar-text {
      align-self: center;
    }
    &.cr-toolbar-selected {
      align-items: center;
      background-color: lighten($primary-color, 42%);
      border-color: $primary-color;
    }
  }
}
</style>
